import { ChangeEvent, FC, ReactNode } from 'react'

import { type FieldMetadata, useInputControl } from '@conform-to/react'
import { z } from 'zod'

import { Checkbox } from '~/components'
import { CheckboxContainer } from '~/features/common/checkbox-container'
import { SearchFormSectionLayout } from '~/features/common/search-form-sections/section-layout'

export const facilitiesSchema = z.object({
  facilities: z.array(z.string()),
})

export const partialFacilitiesSchemaSchema = facilitiesSchema.partial()

type FacilitiesType = z.infer<typeof facilitiesSchema>['facilities']

type CheckBoxListProps = {
  options: { label: ReactNode; facilityId: string }[]
  selectedFacilities: string[]
  initialSelectedFacilities?: string[]
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}
const CheckboxList: FC<CheckBoxListProps> = ({
  options,
  onChange,
  selectedFacilities,
}) =>
  options.map(({ label, facilityId }) => {
    return (
      <Checkbox
        checked={selectedFacilities?.includes(facilityId)}
        key={facilityId}
        label={label}
        onChange={onChange}
        textSize="sm"
        value={facilityId}
      />
    )
  })

export type CheckBoxOptions = {
  label: ReactNode
  facilityId: string
}[]

type FacilitiesAndServicesSectionProps = {
  options: CheckBoxOptions
  headerClassName?: string
  layoutClassName?: string
  columnCount?: { pc: 2 | 3 }
  meta: FieldMetadata<FacilitiesType>
  value: string[]
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  name: string
}

export const useFacilitiesForm = (
  meta: FieldMetadata<FacilitiesType>,
  changeWatchedFacilities?: (facilities: string[]) => void,
) => {
  const facilitiesControl = useInputControl<FacilitiesType>({
    ...meta,
    initialValue: Array.isArray(meta?.initialValue)
      ? meta.initialValue.filter((val): val is string => val !== undefined)
      : [],
  })

  const value = Array.isArray(facilitiesControl.value)
    ? facilitiesControl.value
    : []

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, value: targetValue } = e.target

    const newValues = checked
      ? [...value, targetValue]
      : value.filter((v) => v !== targetValue)

    changeWatchedFacilities?.(newValues)
    facilitiesControl.change(newValues)
  }

  return { value, onChange, name: meta.name }
}

export const FacilitiesAndServicesSection: FC<
  FacilitiesAndServicesSectionProps
> = ({
  options,
  layoutClassName,
  headerClassName,
  columnCount,
  value,
  onChange,
  name,
}) => {
  return (
    <SearchFormSectionLayout
      headerClassName={headerClassName}
      layoutClassName={layoutClassName}
      title="設備・サービス"
    >
      <div>
        <CheckboxContainer className="md:hidden" columnCount={2} showsGridLines>
          <CheckboxList
            onChange={onChange}
            options={options}
            selectedFacilities={value}
          />
        </CheckboxContainer>
      </div>
      <div>
        <CheckboxContainer
          className="hidden grow p-4 md:flex"
          columnCount={columnCount?.pc ?? 3}
        >
          <CheckboxList
            onChange={onChange}
            options={options}
            selectedFacilities={value}
          />
        </CheckboxContainer>
      </div>
      {/* OPTIMIZE: CheckboxListを1つにするのと、CheckboxListにname={meta.name}を指定することでこのinputは削除できる。 */}
      <input name={name} type="hidden" value={value} />
    </SearchFormSectionLayout>
  )
}
